import { useCallback, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseDebouncedSaveOptions {
  articleId: string;
  sectionIndex: number;
  delay?: number;
  onSaveComplete?: () => void;
  onSaveError?: (error: string) => void;
}

export function useDebouncedSave({
  articleId,
  sectionIndex,
  delay = 1000,
  onSaveComplete,
  onSaveError,
}: UseDebouncedSaveOptions) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  const save = useCallback(
    async (content: string) => {
      try {
        setSaveStatus('saving');

        // Cancel any pending request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new abort controller
        abortControllerRef.current = new AbortController();

        const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;

        const response = await fetch(
          `/api/articles/${articleId}/sections/${sectionIndex}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              content,
              wordCount,
            }),
            signal: abortControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Save failed: ${response.statusText}`);
        }

        const now = new Date();
        setLastSaved(now);
        setSaveStatus('saved');

        if (onSaveComplete) {
          onSaveComplete();
        }

        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
      } catch (error) {
        // Ignore abort errors
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        console.error('Failed to save section:', error);
        setSaveStatus('error');

        const errorMessage =
          error instanceof Error ? error.message : 'Failed to save section';

        if (onSaveError) {
          onSaveError(errorMessage);
        }

        // Reset error status after 5 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 5000);
      }
    },
    [articleId, sectionIndex, onSaveComplete, onSaveError]
  );

  const debouncedSave = useCallback(
    (content: string) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        save(content);
      }, delay);
    },
    [save, delay]
  );

  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    debouncedSave,
    saveStatus,
    lastSaved,
    cancelPendingSave,
  };
}